import { useMemo, useState } from 'react';
import { PlatformHeader } from '../../../../shared/platform/PlatformHeader';
import { Modal } from '../../../../shared/ui/Primitives';
import type { AutonomousSummary } from '../../consumer/autonomous';
import type {
  AttentionItem,
  WorkspaceLocation,
  WorkspaceProjection,
  WorkspaceResearchData,
} from '../../consumer/command-workspace-contract';
import { signalHref } from '../../consumer/fire-signals/contract';
import { GEO_UI_KEYS } from '../../consumer/geospatial/config';
import { researchAreaBounds, toGeoJSON } from '../../consumer/geospatial/projection';
import type { MonitoringReadState } from '../../consumer/polling';
import { riskHref } from '../../consumer/workbench';
import { demoFixture, demoKind, type DemoItem } from '../../fixtures/demo-adapter';
import { AutonomousStatus } from '../AutonomousStatus';
import {
  formatRecordTime,
  historicalDateLabel,
  KIND_LABELS,
} from '../command-workspace/WorkspaceCanvas';
import '../command-workspace/command-workspace.css';
import { LiveWeather, useLiveWeather } from './LiveWeather';
import { RegionalEvidence, useRegional } from './RegionalEvidence';
import { RiskDemoMap } from './RiskDemoMap';
import { RiskGisMap } from './RiskGisMap';
import { useForecast, WeatherAttention } from './WeatherAttention';
import { frozenRegionalGeo } from './regional-layer';
import './risk-desktop.css';

export interface RiskDesktopWorkspaceProps {
  projection: WorkspaceProjection;
  location: WorkspaceLocation;
  research: WorkspaceResearchData | null;
  search: string;
  update: (patch: Partial<WorkspaceLocation>) => void;
  retry: () => void;
  errors: string[];
  allAreas: { id: string; name: string }[];
  loading?: boolean;
  autonomous?: MonitoringReadState<AutonomousSummary> | undefined;
}
export function RiskDesktopWorkspace({
  projection,
  location,
  research,
  search,
  update,
  retry,
  errors,
  allAreas,
  loading = false,
  autonomous,
}: RiskDesktopWorkspaceProps) {
  const query = new URLSearchParams(search),
    demo = query.get('dataSource') === 'demo',
    historical = location.mode === 'fire-signals';
  const weather = useLiveWeather(!demo && !historical);
  const forecast = useForecast(historical ? null : demo ? 'DEMO' : 'LIVE');
  const regionalMode = historical ? (demo ? null : 'HISTORICAL_RESEARCH') : demo ? 'DEMO' : 'LIVE';
  const regional = useRegional(regionalMode);
  const [maximized, setMaximized] = useState(false),
    [info, setInfo] = useState(false),
    [detail, setDetail] = useState(false),
    [allRecords, setAllRecords] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [mapFocus, setMapFocus] = useState(false);
  // A backend's implicit first-record highlight must not move the study viewport.
  const [focusSelected, setFocusSelected] = useState(query.has('geoView') ? '' : location.selected);
  const demoArea = query.get('demoArea') ?? '';
  const unavailable = !demo && errors.length > 0;
  const coverageData = autonomous?.status === 'READY' ? autonomous.data : null;
  const coverageInsufficient =
    unavailable ||
    loading ||
    !projection.current ||
    !coverageData ||
    coverageData.dataStatus !== 'READY' ||
    coverageData.runner.status !== 'RUNNING' ||
    coverageData.coverage.eligibleSources === 0 ||
    coverageData.coverage.enabledSources === 0 ||
    coverageData.coverage.healthySources < coverageData.coverage.enabledSources;
  const coverageLabel = historical
    ? `${demo ? '历史演示' : '历史研究'} · 非实时 · NOT_VERIFIED（火情未核实）`
    : demo
      ? '演示覆盖 · 不代表真实风险'
      : coverageInsufficient
        ? `监测覆盖${coverageData?.dataStatus === 'STALE' ? '延迟' : '不足或未知'} · 不能可靠判断`
        : '合成监测覆盖有效 · 不代表区域安全';
  const coverageDetail = historical
    ? demo
      ? `${demoFixture.historicalResearch.date} · 历史示例 · NOT_VERIFIED`
      : historicalDateLabel(projection.items)
    : coverageData
      ? `健康 ${coverageData.coverage.healthySources} / 启用 ${coverageData.coverage.enabledSources} · 合格 ${coverageData.coverage.eligibleSources} · 未知 ${coverageData.coverage.unknownSources} · ${coverageData.asOf}`
      : '尚未获得可用的覆盖状态；无记录不表示零风险。';
  function navigate(patch: Record<string, string | null>, replace = false) {
    const next = new URLSearchParams(window.location.search);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    }
    window.history[replace ? 'replaceState' : 'pushState'](
      null,
      '',
      `${window.location.pathname}?${next}`,
    );
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
  function switchSource(source: 'demo' | 'live') {
    setInfo(false);
    setDetail(false);
    navigate({ dataSource: source, selected: null, filter: 'all' });
  }
  function switchView(view: 'monitoring' | 'historical') {
    setInfo(false);
    setDetail(false);
    navigate({
      view,
      mode: view === 'historical' ? 'fire-signals' : 'monitor',
      selected: null,
      filter: 'all',
      demoArea: null,
    });
  }
  const demoItems: DemoItem[] = historical
    ? demoFixture.historicalResearch.items.slice().sort((a, b) => b.time.localeCompare(a.time))
    : demoFixture.monitoring.items
        .filter((item) => !demoArea || item.regionId === demoArea)
        .sort((a, b) => Date.parse(b.changedAt) - Date.parse(a.changedAt));
  const visibleDemo = demoItems.filter(
    (item) => location.filter === 'all' || demoKind(item) === location.filter,
  );
  const scopedItems = unavailable
    ? []
    : projection.items.filter((item) => location.filter === 'all' || item.kind === location.filter);
  const eligibleGeo = useMemo(
    () =>
      toGeoJSON(
        !demo && historical ? research : null,
        search,
        new Set(projection.items.map((item) => item.id)),
      ),
    [demo, historical, research, search, projection.items],
  );
  const eligibleIds = new Set(eligibleGeo.nasa.features.map((feature) => feature.properties.id));
  const displayIds = new Set(scopedItems.map((item) => item.id));
  const legacyGeo = {
    ...eligibleGeo,
    nasa: {
      ...eligibleGeo.nasa,
      features: eligibleGeo.nasa.features.filter((feature) =>
        displayIds.has(feature.properties.id),
      ),
    },
  };
  const geo =
    !demo && historical && !research ? frozenRegionalGeo(regional.data, search) : legacyGeo;
  const visibleIds = new Set(geo.nasa.features.map((feature) => feature.properties.id));
  const liveItems = historical
    ? scopedItems.filter((item) => visibleIds.has(item.id))
    : scopedItems;
  const selection = demo
    ? (visibleDemo.find((item) => item.id === location.selected) ??
      (!location.selected ? visibleDemo[0] : null))
    : (liveItems.find((item) => item.id === location.selected) ??
      (!location.selected ? liveItems[0] : null));
  const selectedSource = demo
    ? demoFixture.monitoring.sources.find((source) => source.id === location.selected)
    : null;
  const selectedId = selection?.id ?? selectedSource?.id ?? location.selected ?? '';
  const monitoring = demoFixture.monitoring.summary,
    satellite = demoFixture.historicalResearch.summary;
  const kpis = demo
    ? historical
      ? [
          {
            key: 'satellites',
            label: '历史热异常',
            value: satellite.satelliteCandidateCount,
            note: '演示历史 namespace 全部样本',
          },
          {
            key: 'linked',
            label: '天气背景关联',
            value: satellite.regionalWeatherContextCount,
            note: '仅示例区域天气背景，不证明火情',
          },
          {
            key: 'unlinked',
            label: '未关联背景',
            value: satellite.withoutWeatherContextCount,
            note: '示例历史样本',
          },
          {
            key: 'verified',
            label: '独立火情核实',
            value: satellite.verifiedFireEvents,
            note: 'NOT_VERIFIED，不作真实火情结论',
          },
        ]
      : [
          {
            key: 'warnings',
            label: '观测预警',
            value: monitoring.observationWarnings,
            note: '全部演示区域：2条观测预警',
          },
          {
            key: 'forecasts',
            label: '预测关注',
            value: monitoring.forecastWatches,
            note: '全部演示区域：1条独立预测关注',
          },
          {
            key: 'coverage',
            label: '来源覆盖',
            value: `${monitoring.sourcesHealthy} / ${monitoring.sourcesTotal}`,
            note: '全部演示区域：健康来源/全部来源',
          },
          {
            key: 'pending',
            label: '待核对',
            value: monitoring.pendingForecastEvaluations,
            note: '全部演示区域待评价预测数；非真实待办',
          },
        ]
    : projection.kpis.length
      ? projection.kpis.map((kpi) => ({ ...kpi, value: unavailable ? null : kpi.value }))
      : ['观测预警', '预测关注', '来源覆盖', '待核对'].map((label, i) => ({
          key: `unknown-${i}`,
          label,
          value: null,
          note: '当前资料未知',
        }));
  const filters = historical
    ? (['all', 'linked', 'unlinked'] as const)
    : (['all', 'warning', 'forecast'] as const);
  const researchQuery = new URLSearchParams(search);
  for (const key of [
    'dataSource',
    'view',
    'mode',
    'demoArea',
    'filter',
    'selected',
    'areaId',
    ...GEO_UI_KEYS,
  ])
    researchQuery.delete(key);
  const completeHref = historical
    ? signalHref(researchQuery.toString())
    : riskHref('', location.areaId, { view: 'records' });
  function select(id: string) {
    setFocusSelected(id);
    // Keep the current map surface and viewport when selecting a map/list item.
    navigate({ selected: id });
  }
  function record(item: DemoItem | AttentionItem) {
    const isDemo = 'point' in item,
      kind = isDemo ? demoKind(item) : item.kind;
    const status = isDemo
      ? item.kind === 'SATELLITE_SAMPLE'
        ? item.linkedWeather
          ? '已关联背景'
          : '未关联背景'
        : item.kind === 'FORECAST_WATCH'
          ? '独立预测 · 待核对'
          : '合成观测异常'
      : item.status;
    return (
      <article
        className={`risk-record risk-${historical ? 'satellite' : kind}`}
        key={item.id}
        data-r2-record
        data-id={item.id}
        data-selected={selectedId === item.id}
      >
        <button
          type="button"
          className="risk-record-select"
          data-vs08-item-id={item.id}
          aria-pressed={selectedId === item.id}
          onClick={() => select(item.id)}
        >
          <i className="risk-record-dot" />
          {historical && (
            <span className="risk-record-thumbnail" aria-hidden="true">
              ◉
            </span>
          )}
          <span className="risk-record-copy">
            <strong>{item.title}</strong>
            <time>{isDemo ? item.time : formatRecordTime(item.time, historical)}</time>
            <span>
              {isDemo
                ? item.kind === 'SATELLITE_SAMPLE'
                  ? `FRP ${item.frpMw} MW · 亮温 ${item.brightnessK} K（示例）`
                  : item.description
                : item.candidate
                  ? `${item.candidate.satellite.longitude}, ${item.candidate.satellite.latitude} · FRP ${item.candidate.satellite.frpMW ?? '未知'} MW${item.candidate.weather.metrics.TEMPERATURE ? ` · 区域天气 ${item.candidate.weather.metrics.TEMPERATURE.distanceKm.toFixed(3)} km` : ''}`
                  : item.reason}
            </span>
            <em>{status}</em>
          </span>
        </button>
        {isDemo ? (
          <button
            type="button"
            className="risk-record-detail"
            aria-label="查看演示详情"
            onClick={() => {
              select(item.id);
              setDetail(true);
            }}
          >
            详情 →
          </button>
        ) : (
          item.href && (
            <a className="risk-record-detail" href={item.href}>
              详情 →
            </a>
          )
        )}
      </article>
    );
  }
  return (
    <div
      className="p01 risk-desktop-page"
      data-r2-workspace
      data-map-focus={mapFocus}
      data-source={demo ? 'demo' : 'live'}
      data-view={historical ? 'historical' : 'monitoring'}
      data-vs08-workspace
      data-mode={location.mode}
      data-vs09-identity={
        demo ? 'DEMO' : historical ? 'HISTORICAL_RESEARCH' : 'MONITORING_SYNTHETIC'
      }
    >
      <PlatformHeader activePage="monitoring" monitoringMode={location.mode} />
      <main id="nm-main" tabIndex={-1} className="risk-desktop-shell">
        <header className="risk-title-strip" data-r2-title-strip>
          <div className="risk-title-copy">
            <div>
              <h1 data-r2-title data-vs08-title>
                风险与预警
              </h1>
              <span className="risk-workspace-badge">◇ 空间态势工作台</span>
            </div>
            <p>
              {demo ? (
                <span className="risk-demo-notice" data-r2-demo-notice>
                  示例数据 · 非实时 · 不用于救援
                </span>
              ) : historical ? (
                '卫星历史研究 · 非实时 · 火情未核实'
              ) : (
                <>
                  合成监测 · 非运营 ·{' '}
                  {autonomous ? <AutonomousStatus state={autonomous} /> : '时间与范围见读取摘要'}
                </>
              )}
            </p>
          </div>
          <section
            id="risk-workspace-kpis"
            className="risk-compact-kpis"
            hidden={mapFocus}
            aria-label={demo ? '全部演示区域统计，筛选不改变总计' : '关键状态指标'}
          >
            {kpis.map((kpi, i) => (
              <article data-r2-kpi={kpi.key} data-vs08-kpi={kpi.key} key={kpi.key} title={kpi.note}>
                <i aria-hidden="true">{['▲', '●', '▱', '✓'][i]}</i>
                <div>
                  <span>{kpi.label}</span>
                  <strong data-value={kpi.value ?? 'UNKNOWN'}>{kpi.value ?? '—'}</strong>
                </div>
              </article>
            ))}
          </section>
          <div className="risk-heading-actions">
            <button
              type="button"
              className="risk-focus-button"
              aria-pressed={mapFocus}
              aria-controls="risk-workspace-kpis"
              onClick={() => setMapFocus(!mapFocus)}
            >
              {mapFocus ? '显示概览' : '专注地图'}
            </button>
            <button type="button" className="risk-info-button" onClick={() => setInfo(true)}>
              数据与专业视图 ↗
            </button>
          </div>
        </header>
        <section className="risk-filter-strip" data-r2-filter-strip>
          <label>
            <span>区域：</span>
            <select
              aria-label={demo ? '演示区域' : '区域筛选'}
              data-r2-area
              value={demo ? demoArea : historical ? location.areaRef : location.areaId}
              onChange={(event) => {
                setFocusSelected('');
                if (demo) navigate({ demoArea: event.target.value, selected: null });
                else if (historical)
                  navigate({
                    areaRef: event.target.value,
                    selected: null,
                    geoView: null,
                    geoBbox: null,
                  });
                else update({ areaId: event.target.value, selected: '' });
              }}
            >
              <option value="">
                {demo ? '全部示例区域' : historical ? '全部研究区' : '全部监测区'}
              </option>
              {demo
                ? !historical &&
                  demoFixture.monitoring.regions.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.name}
                    </option>
                  ))
                : historical
                  ? research?.summary.areas.map((area) => (
                      <option key={area.areaRef} value={area.areaRef}>
                        {area.name}
                      </option>
                    ))
                  : allAreas.map((area) => (
                      <option key={area.id} value={area.id}>
                        {area.name}
                      </option>
                    ))}
            </select>
          </label>
          <div className="risk-type-filters" role="group" aria-label="关注类别">
            {filters.map((filter) => (
              <button
                type="button"
                key={filter}
                data-r2-filter={filter}
                data-vs08-filter={filter}
                aria-pressed={location.filter === filter}
                onClick={() => update({ filter, selected: '' })}
              >
                {filter === 'all' ? '全部' : KIND_LABELS[filter]}
              </button>
            ))}
          </div>
          <div
            className="risk-coverage-strip"
            data-mapfirst-coverage
            data-warning={!demo && !historical && coverageInsufficient}
            role="status"
            title={coverageDetail}
          >
            <strong>{coverageLabel}</strong>
            <a href={riskHref('/network', location.areaId)}>查看覆盖 ↗</a>
          </div>
        </section>
        <section className="risk-desktop-main" data-r2-main data-maximized={maximized || collapsed}>
          <section
            className="risk-map-pane"
            aria-label={
              demo
                ? '演示地图工作区'
                : historical
                  ? '真实 GIS 历史研究地图'
                  : '真实 GIS 背景 · 合成监测来源无地理位置'
            }
          >
            {demo && (historical || query.get('weatherGrid') !== '1') ? (
              <RiskDemoMap
                items={visibleDemo}
                selected={selectedId}
                select={(id) => {
                  select(id);
                  if (id.startsWith('DEMO-SRC-')) setDetail(true);
                }}
                historical={historical}
                maximized={maximized}
                maximize={() => setMaximized(!maximized)}
              />
            ) : (
              <RiskGisMap
                weather={weather.data}
                forecast={forecast.data}
                weatherAreaId={location.areaId}
                geo={geo}
                historical={historical}
                loading={loading}
                unavailable={unavailable}
                selected={selectedId}
                search={search}
                focusSelected={focusSelected}
                areaRef={location.areaRef}
                studyBounds={researchAreaBounds(research?.summary.areas ?? [], location.areaRef)}
                select={select}
                navigate={navigate}
                retry={retry}
                maximized={maximized}
                maximize={() => setMaximized(!maximized)}
                collapsed={collapsed}
                collapse={() => setCollapsed(!collapsed)}
              />
            )}
          </section>
          <aside className="risk-attention-pane" data-r2-attention aria-label="需要关注">
            <header>
              <h2>需要关注</h2>
              <span>按最近变化排序</span>
            </header>
            <div className="risk-attention-tabs">
              {filters.map((filter) => (
                <button
                  type="button"
                  key={filter}
                  aria-pressed={location.filter === filter}
                  onClick={() => update({ filter, selected: '' })}
                >
                  {filter === 'all' ? '全部' : KIND_LABELS[filter]} (
                  {demo
                    ? demoItems.filter((item) => filter === 'all' || demoKind(item) === filter)
                        .length
                    : unavailable || loading || (!historical && !projection.current)
                      ? '—'
                      : (historical
                          ? projection.items.filter((item) => eligibleIds.has(item.id))
                          : projection.items
                        ).filter((item) => filter === 'all' || item.kind === filter).length}
                  )
                </button>
              ))}
            </div>
            <div className="risk-attention-list" data-r2-queue>
              {!historical && (
                <WeatherAttention
                  {...forecast}
                  onSelect={(id) => navigate({ selected: id })}
                  selectedId={location.selected}
                  mode={demo ? 'DEMO' : 'LIVE'}
                  areaId={demo ? '' : location.areaId}
                  focus={(longitude, latitude, id) =>
                    navigate({
                      geoView: `${longitude},${latitude},12,0,0`,
                      weatherGrid: '1',
                      selected: id,
                    })
                  }
                />
              )}
              <RegionalEvidence
                state={regional}
                selectedId={location.selected}
                mode={historical ? (demo ? null : 'HISTORICAL_RESEARCH') : demo ? 'DEMO' : 'LIVE'}
                areaId={historical ? location.areaRef : location.areaId}
                focus={(longitude, latitude, id) =>
                  navigate({
                    geoView: `${longitude},${latitude},10,0,0`,
                    selected: id,
                    weatherGrid: historical ? null : '1',
                  })
                }
              />
              {!demo && !historical && (
                <LiveWeather
                  {...weather}
                  selectedId={location.selected}
                  areaId={location.areaId}
                  focus={(longitude, latitude, id) =>
                    navigate({ geoView: `${longitude},${latitude},10,0,0`, selected: id })
                  }
                />
              )}
              {!demo && !historical && autonomous && (
                <details className="risk-empty" data-vs10-coverage>
                  <summary>监测来源与覆盖详情</summary>
                  <AutonomousStatus state={autonomous} areaId={location.areaId} details />
                </details>
              )}
              {(demo ? visibleDemo : liveItems).map(record)}
              {!(demo ? visibleDemo : liveItems).length && (
                <div className="risk-empty">
                  <strong>
                    {unavailable ? '数据连接不可用' : loading ? '正在读取…' : '暂无匹配关注记录'}
                  </strong>
                  <p>
                    {unavailable
                      ? '请重试；也可在数据与专业视图中主动选择示例。'
                      : '无记录不表示区域安全。'}
                  </p>
                  {unavailable && (
                    <button type="button" onClick={retry}>
                      重试
                    </button>
                  )}
                </div>
              )}
            </div>
            <footer>
              <span>
                {!demo && (unavailable || loading || (!historical && !projection.current))
                  ? '记录状态未知'
                  : `共 ${(demo ? visibleDemo : liveItems).length} 条记录`}
              </span>
              {demo ? (
                <button type="button" onClick={() => setAllRecords(true)}>
                  查看全部 →
                </button>
              ) : (
                <a data-vs08-all href={completeHref}>
                  查看全部 →
                </a>
              )}
            </footer>
          </aside>
        </section>
        <footer className="risk-page-footer">
          <span>
            {demo
              ? 'AI 示意地形 · 图像相对坐标 · DEMO_FIXTURE_ONLY'
              : historical
                ? 'NASA FIRMS / NOAA NCEI · 历史研究 · NOT_VERIFIED'
                : '配置关系 · 无地理坐标 · 合成监测'}
          </span>
          <nav aria-label="管理与评估">
            <a href={riskHref('/network', location.areaId)}>区域与来源 ↗</a>
            <a href={riskHref('/evaluation', location.areaId)}>模型效果与回验 ↗</a>
            {demo && historical && (
              <a href={signalHref(researchQuery.toString())}>查看真实历史研究</a>
            )}
            <button type="button" onClick={() => setInfo(true)}>
              数据说明 ⓘ
            </button>
          </nav>
        </footer>
        {!location.valid && (
          <div className="risk-link-error" role="alert">
            工作台链接无效，请检查筛选和区域。
          </div>
        )}
        {info && (
          <Modal title="数据与模式说明" close={() => setInfo(false)}>
            <div className="risk-detail-content">
              {demo ? (
                <>
                  <p>{demoFixture.notice}</p>
                  <p>{demoFixture.map.attribution}</p>
                  <p>
                    示例点位、示意边界与参数属于 DEMO_FIXTURE_ONLY。历史热异常保持
                    NOT_VERIFIED；天气关联仅为背景。示例不产生真实 ACK、来源配置、火情确认或派遣。
                  </p>
                </>
              ) : (
                <p>
                  当前使用既有 Java / SQLite 接口；监测为后端合成数据，历史研究沿用 NASA FIRMS /
                  NOAA NCEI
                  资料。历史热异常尚未核实为火灾；天气关联仅为背景。无监测地理坐标时不放置来源点位，接口失败保留未知状态。MapTiler
                  提供真实底图；NASA / NOAA
                  提供独立历史业务数据。记录详情沿用原有修订、预测和来源身份。
                </p>
              )}
              <h3>数据来源与专业视图</h3>
              <div className="risk-heading-controls">
                <div className="risk-source-switch" role="group" aria-label="数据来源">
                  <button
                    type="button"
                    data-r2-source-choice="live"
                    aria-pressed={!demo}
                    onClick={() => switchSource('live')}
                  >
                    真实接口
                  </button>
                  <button
                    type="button"
                    data-r2-source-choice="demo"
                    aria-pressed={demo}
                    onClick={() => switchSource('demo')}
                  >
                    体验示例
                  </button>
                </div>
                <div className="risk-view-switch" role="group" aria-label="工作视图">
                  <button
                    type="button"
                    data-r2-view="monitoring"
                    aria-pressed={!historical}
                    onClick={() => switchView('monitoring')}
                  >
                    监测态势
                  </button>
                  <button
                    type="button"
                    data-r2-view="historical"
                    aria-pressed={historical}
                    onClick={() => switchView('historical')}
                  >
                    卫星历史信号
                  </button>
                </div>
              </div>
              <p>切换模式不会赋予写入权限。示例仅供演练，历史资料不计入当前风险。</p>
              <p>
                {coverageLabel} · {coverageDetail}
              </p>
              <h3>当前指标定义</h3>
              <dl>
                {kpis.map((kpi) => (
                  <div key={kpi.key}>
                    <dt>{kpi.label}</dt>
                    <dd>{kpi.note}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Modal>
        )}
        {demo && detail && (
          <Modal title="演示示例详情 · DEMO" close={() => setDetail(false)}>
            <div className="risk-detail-content">
              <p className="risk-demo-notice">示例数据 · 非实时 · 不用于救援</p>
              <h3>{selection?.title ?? selectedSource?.name ?? '当前示例记录不在筛选范围'}</h3>
              <code>{selectedId || location.selected}</code>
              {selection && 'point' in selection && (
                <>
                  <p>
                    {selection.kind === 'SATELLITE_SAMPLE'
                      ? '卫星历史热异常演示 · NOT_VERIFIED'
                      : selection.detail}
                  </p>
                  <dl>
                    <div>
                      <dt>图像相对位置</dt>
                      <dd>
                        {selection.point.x}, {selection.point.y} · 非经纬度
                      </dd>
                    </div>
                    <div>
                      <dt>时间</dt>
                      <dd>{selection.time}（示例）</dd>
                    </div>
                    {selection.kind === 'SATELLITE_SAMPLE' && (
                      <>
                        <div>
                          <dt>示例 FRP / 亮温</dt>
                          <dd>
                            {selection.frpMw} MW / {selection.brightnessK} K
                          </dd>
                        </div>
                        <div>
                          <dt>示例 native confidence</dt>
                          <dd>{selection.nativeConfidence}</dd>
                        </div>
                        <div>
                          <dt>区域天气背景</dt>
                          <dd>
                            {selection.linkedWeather
                              ? `已关联 · ${selection.weatherDistanceKm} km（示例）`
                              : '未关联'}
                          </dd>
                        </div>
                      </>
                    )}
                  </dl>
                </>
              )}
              {selectedSource && (
                <p>示例来源状态：{selectedSource.state}。无真实来源 epoch 或管理权限。</p>
              )}
              <p>只读前端演示；不可执行真实操作。</p>
            </div>
          </Modal>
        )}
        {demo && allRecords && (
          <Modal title="全部演示记录 · DEMO" close={() => setAllRecords(false)}>
            <div className="risk-detail-content">
              <p className="risk-demo-notice">示例数据 · 非实时 · 不用于救援</p>
              {demoItems.map((item) => (
                <p key={item.id}>
                  <button
                    type="button"
                    onClick={() => {
                      select(item.id);
                      setAllRecords(false);
                    }}
                  >
                    {item.title} · {item.time}
                  </button>
                </p>
              ))}
            </div>
          </Modal>
        )}
      </main>
    </div>
  );
}
